import { ShareDialogAccessLevel } from "@components/access-level/share-dialog-access-level";
import { CopyLinkButton } from "@components/common/button/copy-link-button";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { SphereActionDivider } from "@components/common/sphere-action-divider";
import { SphereDivider } from "@components/common/sphere-divider";
import { BaseSnapshotProps } from "@custom-types/base-types";
import { Dialog } from "@flat-ui";
import { useHasValidFeatureProjectLevel } from "@hooks/feature-control/use-has-user-valid-feature-project-level";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { InviteMemberToSnapshot } from "@pages/project-details/project-snapshots/invite-member-to-snapshot";
import { SnapshotMembers } from "@pages/project-details/project-snapshots/snapshot-members";
import { getOpenSnapshotHref } from "@pages/project-details/project-snapshots/snapshot-utils";
import { defaultProjectLaunchTargetSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import {
  canUserViewSnapshotMembers,
  isUserSnapshotAdmin,
} from "@utils/permission-control/snapshot-permission-control";
import { SnapshotEvents } from "@utils/track-event/track-event-list";
import { PropsWithChildren } from "react";

/** Locations from which the share snapshot dialog can be opened */
type ShareSnapshotLocation =
  "snapshot-sidepanel" | "snapshot-action" | "snapshot-mobile-menu-options";

interface Props extends BaseSnapshotProps {
  /** Determines if the dialog should open */
  isOpen: boolean;

  /** Function to call when the dialog should close */
  onClose: () => void;

  /** The location from which the dialog was opened, used for event tracking */
  location: ShareSnapshotLocation;
}

/** Reusable share dialog for a snapshot */
export function SnapshotShareDialog({
  snapshot,
  isOpen,
  onClose,
  location,
  children: triggerButton,
}: PropsWithChildren<Props>): JSX.Element {
  const { defaultOpenProjectTarget } = useAppSelector(
    defaultProjectLaunchTargetSelector
  );
  const { canInviteMembersToSnapshot } =
    useHasUserValidPermissionProjectLevel();
  const { canCreateSnapshot } = useHasValidFeatureProjectLevel();

  const canInvite = canInviteMembersToSnapshot && canCreateSnapshot;
  const canViewMembers = canUserViewSnapshotMembers(snapshot);

  return (
    <>
      {triggerButton}

      <Dialog
        open={isOpen}
        onClose={onClose}
        title="Share snapshot"
        topRightActions={
          <>
            <CopyLinkButton
              targetHref={getOpenSnapshotHref({
                openTarget: defaultOpenProjectTarget,
                snapshotId: snapshot.id,
              })}
              logEventParams={{
                name: SnapshotEvents.copySnapshotLink,
                props: {
                  snapshotId: snapshot.id,
                  accessLevel: snapshot.accessLevel,
                  location,
                },
              }}
            />
            <SphereActionDivider />
          </>
        }
      >
        <ShareDialogAccessLevel
          type="snapshot"
          entity={snapshot}
          isAccessLevelEditable={isUserSnapshotAdmin(snapshot)}
          location="snapshot-share-dialog"
        />

        {canInvite && (
          <>
            <SphereDivider sx={{ my: SPACE_ELEMENTS_OF_MODAL }} />
            <InviteMemberToSnapshot snapshot={snapshot} />
          </>
        )}

        {canViewMembers && (
          <>
            <SphereDivider sx={{ mt: SPACE_ELEMENTS_OF_MODAL, mb: "12px" }} />
            <SnapshotMembers
              snapshot={snapshot}
              location="snapshot-share-dialog"
            />
          </>
        )}
      </Dialog>
    </>
  );
}
